import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  NgZone,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { Observable, Subscription, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SliderBase } from '@nimbus-library/features/shared/components/slider/domain/slider-base';
import { SliderChangeEvent } from '@nimbus-library/features/shared/components/slider/domain/slider-change.event';
import { IMicrosoftTeamsContextAccessor } from '@nimbus-library/features/microsoft-teams/contract/i-microsoft-teams-context.accessor';
import { ClientType } from '@nimbus-library/features/microsoft-teams/models/client-type';

const activeEventOptions = normalizePassiveListenerOptions({ passive: false });

const DISABLED_THUMB_GAP = 7;

const MIN_VALUE_NONACTIVE_THUMB_GAP = 7;

const MIN_VALUE_ACTIVE_THUMB_GAP = 10;

export const MAT_SLIDER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => SliderComponent),
  multi: true,
};

function isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
  return event.type[0] === 't';
}

function getPointerPositionOnPage(event: MouseEvent | TouchEvent) {
  const point = isTouchEvent(event) ? event.touches[0] || event.changedTouches[0] : event;
  return { x: point.clientX, y: point.clientY };
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-slider',
  providers: [MAT_SLIDER_VALUE_ACCESSOR],
  host: {
    '(focus)': '_onFocus()',
    '(blur)': '_onBlur()',
    '(mouseenter)': '_onMouseenter()',
    '(selectstart)': '$event.preventDefault()',
    class: 'mat-slider mat-slider-horizontal',
    '[class.mat-slider-disabled]': 'disabled',
    '[class.mat-slider-sliding]': '_isSliding',
    '[class.mat-slider-thumb-label-showing]': 'thumbLabel',
    '[class.mat-slider-min-value]': '_isMinValue',
    '[class.mat-slider-hide-last-tick]': 'disabled',
    '[class._mat-animation-noopable]': '_animationMode === "NoopAnimations"',
  },
  templateUrl: './slider.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent extends SliderBase implements ControlValueAccessor, OnDestroy, OnInit {
  public teamsClient$: Observable<boolean> = this.microsoftTeamsContextAccessor
    ? this.microsoftTeamsContextAccessor.getClientInfo$().pipe(
        take(1),
        map((x) => x !== ClientType.Browser),
      )
    : of(false);

  @Input()
  public disabled: boolean;
  @Input()
  public get max(): number {
    return this._max;
  }

  public set max(v: number) {
    this._max = coerceNumberProperty(v, this._max);
    this._percent = this._calculatePercentage(this._value);

    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get min(): number {
    return this._min;
  }

  public set min(v: number) {
    this._min = coerceNumberProperty(v, this._min);

    if (this._value === null) {
      this.value = this._min;
    }
    this._percent = this._calculatePercentage(this._value);

    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get step(): number {
    return this._step;
  }

  public set step(v: number) {
    this._step = coerceNumberProperty(v, this._step);

    if (this._step % 1 !== 0) {
      this._roundToDecimal = this._step.toString().split('.').pop()!.length;
    }

    this._changeDetectorRef.markForCheck();
  }

  @Input()
  public get thumbLabel(): boolean {
    return this._thumbLabel;
  }

  public set thumbLabel(value: boolean) {
    this._thumbLabel = coerceBooleanProperty(value);
  }

  @Input()
  public get value(): number | null {
    if (this._value === null) {
      this.value = this._min;
    }
    return this._value;
  }

  public set value(v: number | null) {
    if (v !== this._value) {
      let value = coerceNumberProperty(v);

      if (this._roundToDecimal) {
        value = parseFloat(value.toFixed(this._roundToDecimal));
      }

      this._value = value;
      this._percent = this._calculatePercentage(this._value);

      this._changeDetectorRef.markForCheck();
    }
  }

  public get percent(): number {
    return this._clamp(this._percent);
  }

  public get _isMinValue() {
    return this.percent === 0;
  }

  public get _thumbGap() {
    if (this.disabled) {
      return DISABLED_THUMB_GAP;
    }
    if (this._isMinValue && !this.thumbLabel) {
      return this._isActive ? MIN_VALUE_ACTIVE_THUMB_GAP : MIN_VALUE_NONACTIVE_THUMB_GAP;
    }
    return 0;
  }

  public get _thumbContainerStyles(): { [key: string]: string } {
    const offset = (1 - this.percent) * 100;
    return {
      transform: `translateX(-${offset}%)`,
    };
  }

  public constructor(
    private readonly microsoftTeamsContextAccessor: IMicrosoftTeamsContextAccessor,
    elementRef: ElementRef,
    private _focusMonitor: FocusMonitor,
    private _changeDetectorRef: ChangeDetectorRef,
    @Optional() private _dir: Directionality,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) public _animationMode?: string,
    private _ngZone?: NgZone,
  ) {
    super(elementRef);

    this._runOutsizeZone(() => {
      const element = elementRef.nativeElement;
      element.addEventListener('mousedown', this._pointerDown, activeEventOptions);
      element.addEventListener('touchstart', this._pointerDown, activeEventOptions);
    });
  }

  private _max: number = 100;

  private _min: number = 0;

  private _step: number = 1;

  private _thumbLabel: boolean = false;

  private _value: number | null = null;

  @Input()
  public displayWith: (value: number) => string | number;

  @Output()
  public readonly eventChange: EventEmitter<SliderChangeEvent<SliderComponent>> = new EventEmitter<SliderChangeEvent<SliderComponent>>();

  @Output()
  public readonly eventInput: EventEmitter<SliderChangeEvent<SliderComponent>> = new EventEmitter<SliderChangeEvent<SliderComponent>>();

  @Output()
  public readonly valueChange: EventEmitter<number | null> = new EventEmitter<number | null>();

  @ViewChild('sliderWrapper', { static: true })
  private _sliderWrapper: ElementRef;

  private _percent: number = 0;

  public _isSliding: boolean = false;

  public _isActive: boolean = false;

  private _sliderDimensions: ClientRect | null = null;

  private _roundToDecimal: number;

  private _dirChangeSubscription = Subscription.EMPTY;

  private _valueOnSlideStart: number | null;

  private _pointerPositionOnStart: { x: number; y: number } | null;

  public focus(options?: FocusOptions) {
    this._focusHostElement(options);
  }

  public onTouched: () => any = () => {};

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  public ngOnInit(): void {
    this._focusMonitor.monitor(this._elementRef, true).subscribe((origin: FocusOrigin) => {
      this._isActive = !!origin && origin !== 'keyboard';
      this._changeDetectorRef.detectChanges();
    });
    if (this._dir) {
      this._dirChangeSubscription = this._dir.change.subscribe(() => {
        this._changeDetectorRef.markForCheck();
      });
    }
  }

  public ngOnDestroy(): void {
    const element = this._elementRef.nativeElement;
    element.removeEventListener('mousedown', this._pointerDown, activeEventOptions);
    element.removeEventListener('touchstart', this._pointerDown, activeEventOptions);
    this._removeGlobalEvents();
    this._focusMonitor.stopMonitoring(this._elementRef);
    this._dirChangeSubscription.unsubscribe();
  }

  public _onMouseenter(): void {
    if (this.disabled) {
      return;
    }

    this._sliderDimensions = this._getSliderDimensions();
  }

  public _onFocus(): void {
    this._sliderDimensions = this._getSliderDimensions();
  }

  public _onBlur(): void {
    this.onTouched();
  }

  private _pointerDown = (event: TouchEvent | MouseEvent): void => {
    if (this.disabled || this._isSliding || (!isTouchEvent(event) && event.button !== 0)) {
      return;
    }

    this._runInsideZone(() => {
      const oldValue = this.value;
      const pointerPosition = getPointerPositionOnPage(event);

      this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
        if (isTeamsClient) {
          const browserScale = this.getBrowserScalePercentage();
          pointerPosition.x = (pointerPosition.x / 100) * browserScale;
          pointerPosition.y = (pointerPosition.y / 100) * browserScale;
        }
      });

      this._isSliding = true;
      event.preventDefault();
      this._focusHostElement();
      this._onMouseenter(); // Simulate mouseenter in case this is a mobile device.
      this._bindGlobalEvents(event);
      this._focusHostElement();
      this._updateValueFromPosition(pointerPosition);
      this._valueOnSlideStart = this.value;
      this._pointerPositionOnStart = pointerPosition;

      if (oldValue !== this.value) {
        this._emitInputEvent();
        this._emitChangeEvent();
      }
    });
  };

  private getBrowserScalePercentage(): number {
    const isScaled = (this._elementRef.nativeElement as HTMLDivElement).classList.contains('scaled');
    return isScaled ? Math.round(window.devicePixelRatio * 100) : 100;
  }

  private _pointerMove = (event: TouchEvent | MouseEvent): void => {
    if (this._isSliding) {
      event.preventDefault();
      const oldValue = this.value;
      const pointerPosition = getPointerPositionOnPage(event);

      this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
        if (isTeamsClient) {
          const browserScale = this.getBrowserScalePercentage();
          pointerPosition.x = (pointerPosition.x / 100) * browserScale;
          pointerPosition.y = (pointerPosition.y / 100) * browserScale;
        }
        this._updateValueFromPosition(pointerPosition);

        if (oldValue !== this.value) {
          this._emitInputEvent();
        }
      });
    }
  };

  private _pointerUp = (event: TouchEvent | MouseEvent): void => {
    if (this._isSliding) {
      const pointerPositionOnStart = this._pointerPositionOnStart;

      const currentPointerPosition = getPointerPositionOnPage(event);

      this.teamsClient$.pipe(untilDestroyed(this)).subscribe((isTeamsClient) => {
        if (isTeamsClient) {
          const browserScale = this.getBrowserScalePercentage();
          currentPointerPosition.x = (currentPointerPosition.x / 100) * browserScale;
          currentPointerPosition.y = (currentPointerPosition.y / 100) * browserScale;
        }
      });

      event.preventDefault();
      this._removeGlobalEvents();
      this._valueOnSlideStart = this._pointerPositionOnStart = null;
      this._isSliding = false;

      if (
        this._valueOnSlideStart !== this.value &&
        !this.disabled &&
        pointerPositionOnStart &&
        (pointerPositionOnStart.x !== currentPointerPosition.x || pointerPositionOnStart.y !== currentPointerPosition.y)
      ) {
        this._emitChangeEvent();
      }
    }
  };

  public _getTrackFillStyles(): { [key: string]: string } {
    const percent = this.percent * 100;
    return {
      width: `${percent}%`,
      transform: 'translateX(0px)',
    };
  }

  private _bindGlobalEvents(triggerEvent: TouchEvent | MouseEvent): void {
    if (typeof document !== 'undefined' && document) {
      const body = document.body;
      const isTouch = isTouchEvent(triggerEvent);
      const moveEventName = isTouch ? 'touchmove' : 'mousemove';
      const endEventName = isTouch ? 'touchend' : 'mouseup';
      body.addEventListener(moveEventName, this._pointerMove, activeEventOptions);
      body.addEventListener(endEventName, this._pointerUp, activeEventOptions);

      if (isTouch) {
        body.addEventListener('touchcancel', this._pointerUp, activeEventOptions);
      }
    }
  }

  private _removeGlobalEvents(): void {
    if (typeof document !== 'undefined' && document) {
      const body = document.body;
      body.removeEventListener('mousemove', this._pointerMove, activeEventOptions);
      body.removeEventListener('mouseup', this._pointerUp, activeEventOptions);
      body.removeEventListener('touchmove', this._pointerMove, activeEventOptions);
      body.removeEventListener('touchend', this._pointerUp, activeEventOptions);
      body.removeEventListener('touchcancel', this._pointerUp, activeEventOptions);
    }
  }

  private _updateValueFromPosition(pos: { x: number; y: number }): void {
    if (!this._sliderDimensions) {
      return;
    }

    const offset = this._sliderDimensions.left;
    const size = this._sliderDimensions.width;
    const posComponent = pos.x;

    const percent = this._clamp((posComponent - offset) / size);

    if (percent === 0) {
      this.value = this.min;
    } else if (percent === 1) {
      this.value = this.max;
    } else {
      const exactValue = this._calculateValue(percent);

      const closestValue = Math.round((exactValue - this.min) / this.step) * this.step + this.min;

      this.value = this._clamp(closestValue, this.min, this.max);
    }
  }

  private _emitChangeEvent(): void {
    this._controlValueAccessorChangeFn(this.value);
    this.valueChange.emit(this.value);
    this.eventChange.emit(this._createChangeEvent());
  }

  private _emitInputEvent(): void {
    this.eventInput.emit(this._createChangeEvent());
  }

  private _createChangeEvent(value = this.value): SliderChangeEvent<SliderComponent> {
    const result = new SliderChangeEvent<SliderComponent>();
    result.source = this;
    result.value = value;
    return result;
  }

  private _calculatePercentage(value: number | null): number {
    return ((value || 0) - this.min) / (this.max - this.min);
  }

  private _calculateValue(percentage: number): number {
    return this.min + percentage * (this.max - this.min);
  }

  private _clamp(value: number, min = 0, max = 1): number {
    return Math.max(min, Math.min(value, max));
  }

  private _getSliderDimensions() {
    return this._sliderWrapper ? this._sliderWrapper.nativeElement.getBoundingClientRect() : null;
  }

  private _focusHostElement(options?: FocusOptions): void {
    this._elementRef.nativeElement.focus(options);
  }

  private _runInsideZone(fn: () => any): void {
    this._ngZone ? this._ngZone.run(fn) : fn();
  }

  private _runOutsizeZone(fn: () => any): void {
    this._ngZone ? this._ngZone.runOutsideAngular(fn) : fn();
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
